import moment from 'moment';
import PropTypes from 'prop-types';

// All the copy of the recipient picker lives here, so the ticket's wording is read and changed in one place.
export const RECIPIENT_LIST_LEGEND = 'Send notification to:';
export const RECIPIENTS_LOADING_MESSAGE = 'Loading recipients...';
export const RECIPIENTS_LOAD_ERROR_MESSAGE = 'The recipients could not be loaded.';
export const NO_RECIPIENT_SELECTED_MESSAGE = 'Select at least one recipient, or clear Notify customer.';
export const NO_RECIPIENT_MATCHES_MESSAGE = 'No recipient matches your search.';
// Not in the ticket's text; this wording was approved for SYN-3081.
export const NO_ADDRESSES_MESSAGE = 'This company has no addresses to notify. Clear Notify customer to enrol without an email.';

// What the recipients request has produced. The fetch hook is the single owner of this value; the modal, the
// status line and the Confirm rule all read it instead of re-deriving it from loading/error/length.
export const RECIPIENTS_STATUS = Object.freeze({
	LOADING: 'loading',
	FAILED: 'failed',
	EMPTY: 'empty',
	READY: 'ready'
});

// The single definition of a recipient on the wire (see the SYN-3081 contract); the list's PropTypes use it.
export const recipientShape = PropTypes.shape({
	email: PropTypes.string.isRequired,
	name: PropTypes.string,
	roles: PropTypes.arrayOf(PropTypes.string).isRequired,
	requestedAccessAt: PropTypes.string,
	selectedByDefault: PropTypes.bool
});

// "More than 3 recipients" in the ticket.
const BULK_SELECTION_MIN_RECIPIENTS = 4;
// "5 or more recipients" in the ticket.
const SEARCH_MIN_RECIPIENTS = 5;
const REQUESTED_ACCESS_DATE_FORMAT = 'D MMM YYYY';

export const getDefaultSelectedEmails = (recipients) => {
	return recipients
		.filter((recipient) => {
			return recipient.selectedByDefault;
		})
		.map((recipient) => {
			return recipient.email;
		});
};

export const filterRecipients = (recipients, searchText) => {
	const searchTerm = searchText.trim().toLowerCase();

	if (!searchTerm) {
		return recipients;
	}

	return recipients.filter((recipient) => {
		return (recipient.name || '').toLowerCase().includes(searchTerm)
			|| recipient.email.toLowerCase().includes(searchTerm);
	});
};

export const hasBulkSelection = (recipients) => {
	return recipients.length >= BULK_SELECTION_MIN_RECIPIENTS;
};

export const hasRecipientSearch = (recipients) => {
	return recipients.length >= SEARCH_MIN_RECIPIENTS;
};

export const toggleEmail = (selectedEmails, email) => {
	return selectedEmails.includes(email)
		? selectedEmails.filter((selectedEmail) => {
			return selectedEmail !== email;
		})
		: [...selectedEmails, email];
};

export const addEmails = (selectedEmails, emails) => {
	return [...new Set([...selectedEmails, ...emails])];
};

export const removeEmails = (selectedEmails, emails) => {
	return selectedEmails.filter((selectedEmail) => {
		return !emails.includes(selectedEmail);
	});
};

// The list's order, not the click order, so the request body is stable for the same selection. A Set, because
// this runs on every render of a list that can hold hundreds of rows.
export const getSelectedRecipientEmails = (recipients, selectedEmails) => {
	const selectedEmailSet = new Set(selectedEmails);

	return recipients
		.filter((recipient) => {
			return selectedEmailSet.has(recipient.email);
		})
		.map((recipient) => {
			return recipient.email;
		});
};

// The one place that says zero selected recipients is not enough; the modal passes the answer on.
export const isSelectionRequired = (selectedCount) => {
	return selectedCount === 0;
};

export const getSelectedCountText = (selectedCount) => {
	return `${selectedCount} ${selectedCount === 1 ? 'recipient' : 'recipients'} selected`;
};

// Shown in the browser's own time zone, like every other date in this tab: a request made at 23:30 UTC is the
// next calendar day for Support in Cluj. The API sends ISO 8601 with an offset (`+00:00`); `Z` parses too.
// A missing or unparseable date gives no badge text, never today's date or "Invalid date".
export const formatRequestedAccessBadge = (requestedAccessAt) => {
	if (!requestedAccessAt) {
		return null;
	}

	const requestedAt = moment(requestedAccessAt, moment.ISO_8601, true);

	if (!requestedAt.isValid()) {
		return null;
	}

	return `Requested access · ${requestedAt.format(REQUESTED_ACCESS_DATE_FORMAT)}`;
};

// Without notification Confirm never waits for the list. With it, Confirm needs a loaded, non-empty list and
// at least one recipient picked (`isSelectionMissing`, from `isSelectionRequired`, computed once by the
// modal), so an empty company (EMPTY) keeps it disabled on purpose.
export const isConfirmEnabled = ({ notifyCustomer, status, isSelectionMissing }) => {
	if (!notifyCustomer) {
		return true;
	}

	return status === RECIPIENTS_STATUS.READY && !isSelectionMissing;
};
